import { Fragment, useMemo, useState } from "react";
import type React from "react";
import { ActionIcon, Box, Group, Text, Tooltip } from "@mantine/core";
import { Button, Table } from "@xplatform/design-system";
import { IconChecks, IconX } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import {
  ReviewStatus,
  type JobFileViewModel,
  type RowEdit,
} from "@models/job";
import { MappingResultKind } from "@models/mappingResult";
import { TM_COLUMNS, colSpanOf } from "@utils/resultTableColumns";
import type {
  MappingResultRow,
  MappingResultRowTm,
  TmVariant,
} from "@models/mappingResult";
import type { RowSaveStatus } from "@hooks/useJobResults";
import TmJobResultsTableRow from "./TmJobResultsTableRow";
import { resolveDisplayTarget } from "@utils/rowHelpers";
import { formatTmVehicleKey, tmVehicleKey } from "@utils/variantLabel";
import { orderTmReviewRows } from "./tmBatchGroups";

interface TmJobResultsTableProps {
  rows: MappingResultRowTm[];
  files: JobFileViewModel[];
  edits: Map<number, RowEdit>;
  saveStates: Map<number, RowSaveStatus>;
  jobId: string;
  registerRowRef: (rowIndex: number, el: HTMLTableRowElement | null) => void;
  onAccept: (rowIndex: number, text?: string) => void;
  onReject: (rowIndex: number) => void;
  onRecover: (rowIndex: number) => void;
  onRetrySave: (rowIndex: number) => void;
  onAcceptAll: (rowIndexes: number[]) => Promise<void>;
  onRejectAll: (rowIndexes: number[]) => Promise<void>;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
}

// Derived, never retyped: a column added to TM_COLUMNS without a matching
// bump here used to misalign grouped rows, and nothing failed on it.
/** The vehicle variant a row was extracted under — TM only; OE rows have no
 *  variant axis. Rows are grouped by its six key fields (model code and engine,
 *  the same key as Compare and the Excel export) under one header each. */

const variantOf = (row: MappingResultRow): TmVariant | null =>
  row.kind === MappingResultKind.Tm ? row.source.variant : null;

const COL_SPAN = colSpanOf(TM_COLUMNS);

const TmJobResultsTable: React.FC<TmJobResultsTableProps> = ({
  rows,
  files,
  edits,
  saveStates,
  jobId,
  registerRowRef,
  onAccept,
  onReject,
  onRecover,
  onRetrySave,
  onAcceptAll,
  onRejectAll,
  hasMore,
  loadingMore,
  onLoadMore,
}) => {
  const { t } = useTranslation();
  const [bulkRunning, setBulkRunning] = useState<"accept" | "reject" | null>(
    null,
  );
  // One block per vehicle: the API returns each file's rows in row order, so a
  // vehicle's rows from two files (or split by a detail the key ignores) are
  // brought together here.
  const orderedRows = useMemo(() => orderTmReviewRows(rows), [rows]);
  const anyVehicle = useMemo(
    () => orderedRows.some((row) => tmVehicleKey(variantOf(row)) !== ""),
    [orderedRows],
  );

  // "All" means the pending rows on screen: the header acts on what the
  // current filter shows, and never overturns a decision already made. A row
  // mid-save or failed is skipped for the same reason its own buttons are
  // disabled.
  const { acceptable, rejectable } = useMemo(() => {
    const pending = rows.filter((row) => {
      const edit = edits.get(row.rowIndex);
      return (
        row.reviewStatus !== ReviewStatus.Failed &&
        saveStates.get(row.rowIndex)?.state !== "saving" &&
        (edit?.reviewStatus ?? row.reviewStatus) === ReviewStatus.Pending
      );
    });
    return {
      acceptable: pending
        .filter((row) => resolveDisplayTarget(row, edits.get(row.rowIndex)) !== null)
        .map((row) => row.rowIndex),
      rejectable: pending.map((row) => row.rowIndex),
    };
  }, [rows, edits, saveStates]);

  const runBulk = async (
    kind: "accept" | "reject",
    rowIndexes: number[],
  ): Promise<void> => {
    setBulkRunning(kind);
    try {
      await (kind === "accept" ? onAcceptAll : onRejectAll)(rowIndexes);
    } finally {
      setBulkRunning(null);
    }
  };

  const acceptAllLabel = t("resultRow.actions.acceptAll", {
    defaultValue: "Accept all",
  });
  const rejectAllLabel = t("resultRow.actions.rejectAll", {
    defaultValue: "Reject all",
  });

  return (
    <Box className="job-results-page__table-wrap job-results-page__table-wrap--tm">
      <Table
        striped
        withTableBorder
        withColumnBorders
        verticalSpacing="sm"
        horizontalSpacing="sm"
        layout="fixed"
        className="job-results-page__table job-results-page__table--tm"
      >
        <colgroup>
          <col style={{ width: "3%" }} />
          <col style={{ width: "13%" }} />
          <col style={{ width: "12%" }} />
          <col style={{ width: "18%" }} />
          <col style={{ width: "18%" }} />
          <col style={{ width: "5%" }} />
          <col style={{ width: "16%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "5%" }} />
        </colgroup>
        <Table.Thead>
          <Table.Tr>
            <Table.Th className="job-results-page__tm-index-head">
              <Text size="xs" c="dimmed" fw={600}>
                {t("jobResultsPage.table.rowIndex", { defaultValue: "#" })}
              </Text>
            </Table.Th>
            <Table.Th>
              {t("jobResultsPage.tmTable.sourcePdf")}
              {files.length > 0 && ` (${files.length})`}
            </Table.Th>
            <Table.Th>
              {t("jobResultsPage.tmTable.sourcePath", {
                defaultValue: "Path",
              })}
            </Table.Th>
            <Table.Th>
              {t("jobResultsPage.tmTable.sourceValue", {
                defaultValue: "Value",
              })}
            </Table.Th>
            <Table.Th>
              {t("jobResultsPage.tmTable.itemMpListing", {
                defaultValue: "Item-MP Listing",
              })}
            </Table.Th>
            <Table.Th>
              {t("jobResultsPage.tmTable.itemMpId", {
                defaultValue: "ItemMP ID",
              })}
            </Table.Th>
            <Table.Th>
              {t("jobResultsPage.tmTable.value", { defaultValue: "value" })}
            </Table.Th>
            <Table.Th className="job-results-page__tm-status-head">
              {t("jobResultsPage.table.status", { defaultValue: "Status" })}
            </Table.Th>
            <Table.Th>
              <Group gap={4} wrap="nowrap" justify="flex-start">
                <Tooltip label={acceptAllLabel}>
                  <ActionIcon
                    size="sm"
                    variant="light"
                    color="teal"
                    onClick={() => void runBulk("accept", acceptable)}
                    disabled={acceptable.length === 0 || bulkRunning !== null}
                    loading={bulkRunning === "accept"}
                    aria-label={acceptAllLabel}
                  >
                    <IconChecks size={14} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label={rejectAllLabel}>
                  <ActionIcon
                    size="sm"
                    variant="light"
                    color="red"
                    onClick={() => void runBulk("reject", rejectable)}
                    disabled={rejectable.length === 0 || bulkRunning !== null}
                    loading={bulkRunning === "reject"}
                    aria-label={rejectAllLabel}
                  >
                    <IconX size={14} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {orderedRows.map((row, idx) => {
            const edit = edits.get(row.rowIndex);
            const variant = variantOf(row);
            const key = tmVehicleKey(variant);
            const prevKey = idx > 0 ? tmVehicleKey(variantOf(orderedRows[idx - 1])) : null;
            // Rows without a vehicle come last, under their own header once a
            // vehicle header has been shown (never in a job without vehicles).
            const showVariantHeader = key !== prevKey && (key !== "" || (anyVehicle && idx > 0));
            const saveState = saveStates.get(row.rowIndex);
            return (
              <Fragment key={row.rowIndex}>
                {showVariantHeader && (
                  <Table.Tr className="job-results-page__variant-row">
                    <Table.Td colSpan={COL_SPAN}>
                      <Text className="job-results-page__variant-label">
                        {key === ""
                          ? t("jobResultsPage.table.noVariant", {
                            defaultValue: "No vehicle",
                          })
                          : <>
                            {t("jobResultsPage.table.variant", {
                              defaultValue: "Variant",
                            })}
                            {" · "}
                            {formatTmVehicleKey(variant)}
                          </>}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
                <TmJobResultsTableRow
                  row={row}
                  edit={edit}
                  saveState={saveState}
                  jobId={jobId}
                  hasInputPdf={files.find((file) => file.fileIndex === row.fileIndex)?.hasInputPdf ?? false}
                  registerRef={(el) => registerRowRef(row.rowIndex, el)}
                  onAccept={(text) => onAccept(row.rowIndex, text)}
                  onReject={() => onReject(row.rowIndex)}
                  onRecover={() => onRecover(row.rowIndex)}
                  onRetrySave={() => onRetrySave(row.rowIndex)}
                />
              </Fragment>
            );
          })}
        </Table.Tbody>
      </Table>

      {hasMore && (
        <Group justify="center" p="sm">
          <Button
            variant="subtle"
            size="sm"
            loading={loadingMore}
            onClick={onLoadMore}
          >
            {t("mappingServicePage.main.loadMore")}
          </Button>
        </Group>
      )}
    </Box>
  );
};

export default TmJobResultsTable;
